import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  ChevronLeft, Loader2, Upload, Plus, Trash2, FileText, 
  User, Building2, Mail, Phone, DollarSign, Clock, Send,
  CheckCircle, CreditCard, FileSpreadsheet
} from 'lucide-react';

const AdminCreateOrder = () => {
  const navigate = useNavigate();
  
  // Customer state
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  
  // Customer details
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyAbn, setCompanyAbn] = useState('');
  
  // Order settings
  const [paymentMethod, setPaymentMethod] = useState('invoice');
  const [notes, setNotes] = useState('');
  
  // Items
  const [items, setItems] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Pricing data
  const [technologies, setTechnologies] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState('');
  const [designRate, setDesignRate] = useState(85);
  
  // Submission
  const [submitting, setSubmitting] = useState(false);
  
  // Load initial data
  useEffect(() => {
    loadPricingData();
    loadDesignRate();
    searchCustomers('');
  }, []);
  
  const loadPricingData = async () => {
    try {
      const [techRes, matRes, delRes] = await Promise.all([
        axios.get(`${API}/settings/technologies`),
        axios.get(`${API}/settings/materials`),
        axios.get(`${API}/settings/delivery-options`)
      ]);
      setTechnologies(techRes.data.technologies || []);
      setMaterials(matRes.data.materials || []);
      setDeliveryOptions(delRes.data.delivery_options || []);
      
      // Set default delivery option
      if (delRes.data.delivery_options?.length > 0) {
        setSelectedDelivery(delRes.data.delivery_options[0].delivery_id);
      }
    } catch (error) {
      console.error('Failed to load pricing data:', error);
    }
  };
  
  const loadDesignRate = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/design-rate`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setDesignRate(response.data.hourly_rate || 85);
    } catch (error) {
      console.error('Failed to load design rate:', error);
    }
  };
  
  const searchCustomers = async (query) => {
    try {
      const response = await axios.get(`${API}/admin/customers/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to search customers:', error);
    }
  };
  
  // Debounced customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch.length >= 2) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);
  
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name || '');
    setCustomerEmail(customer.email || '');
    setCustomerPhone(customer.phone || '');
    setCompanyName(customer.company || '');
    setIsNewCustomer(false);
    setCustomerOpen(false);
  };
  
  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setCompanyName('');
    setCompanyAbn('');
    setIsNewCustomer(true);
  };
  
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploadingFile(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/quote/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const fileType = response.data.file_type?.toLowerCase();
        const needsManualPrice = fileType === 'step' || fileType === 'stp' || 
                                 fileType === 'iges' || fileType === 'igs' ||
                                 response.data.volume_needs_manual_entry;
        
        // Get default material and calculate initial price
        const defaultMaterialId = materials.length > 0 ? materials[0].material_id : '';
        const volume = response.data.volume_cm3 || 0;
        let initialPrice = '';
        
        // Always calculate price if we have volume and material (even for STEP files)
        if (volume > 0 && defaultMaterialId) {
          const material = materials.find(m => m.material_id === defaultMaterialId);
          if (material) {
            initialPrice = (volume * material.price_per_cm3).toFixed(2);
          }
        }
        
        setItems(prev => [...prev, {
          file_id: response.data.file_id,
          file_name: response.data.filename,
          file_type: fileType,
          volume_cm3: volume,
          needs_manual_price: needsManualPrice,
          unit_price: initialPrice,  // Pre-populated price (admin can override)
          material_id: defaultMaterialId,
          technology_id: technologies.length > 0 ? technologies[0].tech_id : '',
          quantity: 1,
          design_hours: 0
        }]);
        
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        const detail = error.response?.data?.detail || 'Upload failed';
        toast.error(`${file.name}: ${detail}`);
      }
    }
    
    setUploadingFile(false);
    e.target.value = '';
  };
  
  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      
      const updated = { ...item, [field]: value };
      
      // Auto-recalculate unit_price when material changes (for all files with volume)
      if (field === 'material_id' && item.volume_cm3 > 0) {
        const material = materials.find(m => m.material_id === value);
        if (material) {
          updated.unit_price = (item.volume_cm3 * material.price_per_cm3).toFixed(2);
        }
      }
      
      return updated;
    }));
  };
  
  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };
  
  const getMaterialPrice = (materialId) => {
    const mat = materials.find(m => m.material_id === materialId);
    return mat?.price_per_cm3 || 0;
  };
  
  const getMaterialName = (materialId) => {
    const mat = materials.find(m => m.material_id === materialId);
    return mat?.name || '';
  };
  
  const calculateItemPrice = (item) => {
    const unitPrice = parseFloat(item.unit_price) || 0;
    return unitPrice * (item.quantity || 1);
  };
  
  const calculateItemDesignCost = (item) => {
    return (parseFloat(item.design_hours) || 0) * designRate;
  };
  
  const calculateTotals = () => {
    let partsSubtotal = 0;
    let totalDesign = 0;
    
    items.forEach(item => {
      partsSubtotal += calculateItemPrice(item);
      totalDesign += calculateItemDesignCost(item);
    });
    
    const deliveryOpt = deliveryOptions.find(d => d.delivery_id === selectedDelivery);
    const deliveryCost = deliveryOpt?.price || 0;
    
    const subtotalBeforeGst = partsSubtotal + totalDesign + deliveryCost;
    const gst = subtotalBeforeGst * 0.10;
    const total = subtotalBeforeGst + gst;
    
    return {
      partsSubtotal: partsSubtotal.toFixed(2),
      totalDesign: totalDesign.toFixed(2),
      deliveryCost: deliveryCost.toFixed(2),
      gst: gst.toFixed(2),
      total: total.toFixed(2)
    };
  };
  
  const handleSubmit = async () => {
    // Validation
    if (!customerEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (items.length === 0) {
      toast.error('At least one item is required');
      return;
    }
    
    // Validate all items have prices
    for (const item of items) {
      if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
        toast.error(`Please enter a price for ${item.file_name}`);
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      const payload = {
        customer_id: selectedCustomer?.user_id || null,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        company_name: companyName,
        company_abn: companyAbn,
        items: items.map(item => ({
          file_id: item.file_id,
          file_name: item.file_name,
          volume_cm3: item.volume_cm3,
          material_id: item.material_id,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          design_hours: parseFloat(item.design_hours) || 0
        })),
        delivery_option_id: selectedDelivery,
        payment_method: paymentMethod,
        order_type: 'order',  // Always create order, not quote
        notes: notes
      };
      
      const response = await axios.post(`${API}/admin/orders/create-manual`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      toast.success(response.data.message || 'Order created!');
      navigate('/admin/orders');
      
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to create order';
      toast.error(detail);
    }
    
    setSubmitting(false);
  };
  
  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link to="/admin/orders">
              <Button variant="outline" size="icon" className="border-white/20 text-white hover:bg-white/5 rounded-none">
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <span className="overline text-[#CCFF00]">Admin</span>
              <h1 className="text-2xl font-['Chivo'] font-black text-white">Create Order</h1>
              <p className="text-white/50 text-sm">Create invoice for walk-in customers or phone orders</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Customer Selection */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#CCFF00]" />
                  Customer Details
                </h2>
                
                <div className="space-y-4">
                  {/* Customer Type Toggle */}
                  <div className="flex gap-2 mb-4">
                    <Button
                      variant={isNewCustomer ? "outline" : "default"}
                      className={!isNewCustomer 
                        ? "bg-[#CCFF00] text-black hover:bg-[#B3E600] flex-1" 
                        : "border-white/20 text-white hover:bg-white/5 flex-1"
                      }
                      onClick={() => setIsNewCustomer(false)}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Existing Customer
                    </Button>
                    <Button
                      variant={isNewCustomer ? "default" : "outline"}
                      className={isNewCustomer 
                        ? "bg-[#CCFF00] text-black hover:bg-[#B3E600] flex-1" 
                        : "border-white/20 text-white hover:bg-white/5 flex-1"
                      }
                      onClick={() => { setIsNewCustomer(true); setSelectedCustomer(null); }}
                      data-testid="new-customer-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Customer
                    </Button>
                  </div>

                  {/* Customer Search - Only show when NOT new customer */}
                  {!isNewCustomer && (
                    <div>
                      <Label className="text-white/70">Search Existing Customer</Label>
                      <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal bg-white/5 border-white/10 text-white mt-1"
                            data-testid="customer-search-btn"
                          >
                            {selectedCustomer ? (
                              <span>{selectedCustomer.name} ({selectedCustomer.email})</span>
                            ) : (
                              <span className="text-white/50">Click to search by name, email, or company...</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0 bg-[#111] border-white/10" align="start">
                          <Command className="bg-transparent" shouldFilter={false}>
                            <CommandInput 
                              placeholder="Type to search customers..." 
                              value={customerSearch}
                              onValueChange={setCustomerSearch}
                              className="border-none focus:ring-0"
                            />
                            <CommandList>
                              {customers.length === 0 ? (
                                <div className="py-6 text-center text-white/50">
                                  No customers found.
                                  <Button 
                                    variant="link" 
                                    className="text-[#CCFF00] block mx-auto mt-2"
                                    onClick={() => { setIsNewCustomer(true); setSelectedCustomer(null); setCustomerOpen(false); }}
                                  >
                                    + Add New Customer Instead
                                  </Button>
                                </div>
                              ) : (
                                <CommandGroup>
                                  {customers.map((customer) => (
                                    <CommandItem
                                      key={customer.user_id || customer.email}
                                      value={customer.email}
                                      onSelect={() => selectCustomer(customer)}
                                      className="cursor-pointer hover:bg-white/5"
                                    >
                                      <div>
                                        <p className="text-white font-medium">{customer.name}</p>
                                        <p className="text-white/50 text-sm">{customer.email}</p>
                                        {customer.company && (
                                          <p className="text-[#CCFF00]/70 text-xs">{customer.company}</p>
                                        )}
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      
                      {selectedCustomer && (
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="text-white/50 mt-1 p-0 h-auto"
                          onClick={clearCustomer}
                        >
                          Clear selection
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Customer Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70">Name *</Label>
                      <Input
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Customer name"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Email *</Label>
                      <Input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="customer@example.com"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Phone</Label>
                      <Input
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="+61 400 000 000"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Company</Label>
                      <Input
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Company name"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-white/70">ABN</Label>
                      <Input
                        value={companyAbn}
                        onChange={(e) => setCompanyAbn(e.target.value)}
                        placeholder="12 345 678 901"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Parts Upload */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#CCFF00]" />
                  Parts
                </h2>
                
                {/* Upload Area */}
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-sm cursor-pointer hover:border-[#CCFF00]/50 transition-colors mb-4">
                  <div className="flex flex-col items-center">
                    {uploadingFile ? (
                      <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-white/50 mb-2" />
                        <p className="text-white/50 text-sm">Drop files or click to upload</p>
                        <p className="text-white/30 text-xs mt-1">STL, STEP, OBJ, 3MF, IGES</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept=".stl,.obj,.3mf,.step,.stp,.iges,.igs"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                  />
                </label>
                
                {/* Items List */}
                {items.length === 0 ? (
                  <p className="text-white/30 text-center py-4">No parts added yet</p>
                ) : (
                  <div className="space-y-4">
                    {items.map((item, index) => (
                      <div key={item.file_id} className="bg-white/5 border border-white/10 rounded-sm p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="text-white font-medium">{item.file_name}</p>
                            <p className="text-white/50 text-xs">
                              {item.file_type?.toUpperCase()} 
                              {item.volume_cm3 > 0 && ` • ${item.volume_cm3.toFixed(2)} cm³`}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(index)}
                            className="text-red-400 hover:text-red-400 hover:bg-red-400/10 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {/* Material Selection */}
                          <div>
                            <Label className="text-white/50 text-xs">Material</Label>
                            <Select
                              value={item.material_id}
                              onValueChange={(v) => updateItem(index, 'material_id', v)}
                            >
                              <SelectTrigger className="bg-white/5 border-white/10 text-white h-9 mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#111] border-white/10">
                                {materials.map(m => (
                                  <SelectItem key={m.material_id} value={m.material_id} className="text-white">
                                    {m.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Quantity */}
                          <div>
                            <Label className="text-white/50 text-xs">Qty</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                              className="bg-white/5 border-white/10 text-white h-9 mt-1"
                            />
                          </div>
                          
                          {/* Price - always editable, pre-populated with calculated value */}
                          <div>
                            <Label className="text-white/50 text-xs">
                              Unit Price ($){item.needs_manual_price ? ' *' : ''}
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.unit_price ?? ''}
                              onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                              placeholder={item.needs_manual_price ? "Enter price" : "0.00"}
                              className="bg-white/5 border-white/10 text-white h-9 mt-1"
                            />
                            {!item.needs_manual_price && item.volume_cm3 > 0 && (
                              <p className="text-white/30 text-xs mt-1">
                                Auto: ${(item.volume_cm3 * getMaterialPrice(item.material_id)).toFixed(2)}
                              </p>
                            )}
                          </div>
                          
                          {/* Design Hours */}
                          <div>
                            <Label className="text-white/50 text-xs">Design Hrs</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={item.design_hours}
                              onChange={(e) => updateItem(index, 'design_hours', e.target.value)}
                              placeholder="0"
                              className="bg-white/5 border-white/10 text-white h-9 mt-1"
                            />
                          </div>
                        </div>
                        
                        {/* Item Total */}
                        <div className="mt-3 pt-3 border-t border-white/10 flex justify-between text-sm">
                          <span className="text-white/50">
                            Part: ${calculateItemPrice(item).toFixed(2)}
                            {item.design_hours > 0 && (
                              <span className="ml-2">+ Design: ${calculateItemDesignCost(item).toFixed(2)}</span>
                            )}
                          </span>
                          <span className="text-[#CCFF00] font-bold">
                            ${(calculateItemPrice(item) + calculateItemDesignCost(item)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Notes */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4">Notes</h2>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special instructions or notes..."
                  className="bg-white/5 border-white/10 text-white min-h-[100px]"
                />
              </div>
            </div>
            
            {/* Sidebar - Order Settings & Summary */}
            <div className="space-y-6">
              {/* Order Settings */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4">Order Settings</h2>
                
                <div className="space-y-4">
                  {/* Payment Method */}
                  <div>
                    <Label className="text-white/70">Payment Method</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <Button
                        variant={paymentMethod === 'invoice' ? 'default' : 'outline'}
                        className={paymentMethod === 'invoice' 
                          ? 'bg-purple-600 text-white hover:bg-purple-700' 
                          : 'border-white/20 text-white hover:bg-white/5'
                        }
                        onClick={() => setPaymentMethod('invoice')}
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Invoice
                      </Button>
                      <Button
                        variant={paymentMethod === 'stripe' ? 'default' : 'outline'}
                        className={paymentMethod === 'stripe' 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'border-white/20 text-white hover:bg-white/5'
                        }
                        onClick={() => setPaymentMethod('stripe')}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Card
                      </Button>
                    </div>
                    {paymentMethod === 'invoice' && (
                      <p className="text-white/50 text-xs mt-2">Payment terms: 14 days</p>
                    )}
                  </div>
                  
                  {/* Delivery Option */}
                  <div>
                    <Label className="text-white/70">Delivery</Label>
                    <Select value={selectedDelivery} onValueChange={setSelectedDelivery}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111] border-white/10">
                        {deliveryOptions.map(d => (
                          <SelectItem key={d.delivery_id} value={d.delivery_id} className="text-white">
                            {d.name} - ${d.price.toFixed(2)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Design Rate Info */}
                  <div className="pt-3 border-t border-white/10">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/50 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Design Rate
                      </span>
                      <span className="text-white">${designRate.toFixed(2)}/hr</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Order Summary */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4">Summary</h2>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Parts ({items.length})</span>
                    <span className="text-white">${totals.partsSubtotal}</span>
                  </div>
                  {parseFloat(totals.totalDesign) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-white/50">Design</span>
                      <span className="text-white">${totals.totalDesign}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-white/50">Delivery</span>
                    <span className="text-white">${totals.deliveryCost}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="text-white/50">GST (10%)</span>
                    <span className="text-white">${totals.gst}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10 text-lg font-bold">
                    <span className="text-white">Total</span>
                    <span className="text-[#CCFF00]">${totals.total}</span>
                  </div>
                </div>
                
                {/* Submit Button */}
                <Button
                  className="w-full mt-6 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold py-6"
                  onClick={handleSubmit}
                  disabled={submitting || items.length === 0}
                  data-testid="submit-order-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Create Order & Send Invoice
                </Button>
                
                <p className="text-white/30 text-xs text-center mt-3">
                  Invoice will be emailed to customer
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminCreateOrder;
